import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Combobox } from '@headlessui/react';
import SearchIcon from '@heroicons/react/outline/SearchIcon';
import { useDecision } from '@optimizely/react-sdk';
import FuzzySearch from 'fuzzy-search';
import { useHotkeys } from 'react-hotkeys-hook';
import { useRouter } from '@tribeplatform/react-sdk';
import { Badge } from '@tribeplatform/react-ui-kit/Badge';
import { Input } from '@tribeplatform/react-ui-kit/Input';
import { Modal } from '@tribeplatform/react-ui-kit/Modal';
import { FeaturesList } from '../common/constants/features';
import { useI18n } from '../i18n';
import { useMemberActionItems } from '../Member/hooks/useMemberActionItems';
import { usePostActionItems } from '../Post/hooks/usePostActionItems';
import { useSpaceActionItems } from '../Space/hooks/useSpaceActionItems';
import { CommandBarAction } from './CommandBarAction';
import { useCommandBar } from './CommandBarContext';
import { useCommandBarAdminSettings } from './hooks/useCommandBarAdminSettings';
import { useCommandBarNavigation } from './hooks/useCommandBarNavigation';
import { useCommandBarPosts } from './hooks/useCommandBarPosts';
import { useCommandBarSearch } from './hooks/useCommandBarSearch';
export const CommandBarHotKey = ({ actionItem, }) => {
    const { push } = useRouter();
    useHotkeys(actionItem === null || actionItem === void 0 ? void 0 : actionItem.hotkey, e => {
        if (!(actionItem === null || actionItem === void 0 ? void 0 : actionItem.action) && !(actionItem === null || actionItem === void 0 ? void 0 : actionItem.url)) {
            return undefined;
        }
        e.preventDefault();
        if (typeof (actionItem === null || actionItem === void 0 ? void 0 : actionItem.action) === 'function') {
            return actionItem.action();
        }
        return push(actionItem === null || actionItem === void 0 ? void 0 : actionItem.url);
    });
    return null;
};
export const CommandBar = () => {
    var _a, _b;
    const intl = useI18n();
    const [rawQuery, setRawQuery] = useState('');
    const { push } = useRouter();
    const query = rawQuery.toLowerCase().replace(/^[#>]/, '');
    const { context: baseContext, open, close, isOpen, options: { context: customContext, excludeActions = [] }, } = useCommandBar();
    const context = customContext !== null && customContext !== void 0 ? customContext : baseContext;
    const onClose = () => {
        close();
        setRawQuery('');
    };
    const runAction = actionItem => {
        onClose();
        if (typeof (actionItem === null || actionItem === void 0 ? void 0 : actionItem.action) === 'function') {
            return actionItem.action();
        }
        push(actionItem === null || actionItem === void 0 ? void 0 : actionItem.url);
    };
    const [decisionCommandBar] = useDecision(FeaturesList.CommandBar);
    useHotkeys('cmd+k,ctrl+k', e => {
        if (isOpen) {
            onClose();
        }
        else {
            open();
        }
        e.preventDefault();
    }, { enabled: decisionCommandBar.enabled });
    const { actions: postsActions } = useCommandBarPosts();
    const { actions: navigationActions } = useCommandBarNavigation();
    const { actions: adminSettingsActions } = useCommandBarAdminSettings();
    const { actions: searchActions } = useCommandBarSearch({
        query: rawQuery,
    });
    const { actions: postActions, shortcuts: postShortcuts, children: postChildren, } = usePostActionItems({
        post: (context === null || context === void 0 ? void 0 : context.entityType) === 'post' ? context === null || context === void 0 ? void 0 : context.entity : null,
        context: 'post',
    });
    const { actions: spaceActions, children: spaceChildren } = useSpaceActionItems({
        space: (context === null || context === void 0 ? void 0 : context.entityType) === 'space' ? context === null || context === void 0 ? void 0 : context.entity : null,
    });
    const { actions: memberActions, children: memberChildren } = useMemberActionItems({
        member: (context === null || context === void 0 ? void 0 : context.entityType) === 'member' ? context === null || context === void 0 ? void 0 : context.entity : null,
        context: 'commandbar',
    });
    if (!decisionCommandBar.enabled) {
        return null;
    }
    const entityActions = [
        ...postActions,
        ...(postShortcuts !== null && postShortcuts !== void 0 ? postShortcuts : []),
        ...memberActions,
        ...spaceActions,
    ];
    const actions = [
        ...(excludeActions.includes('entity') ? [] : entityActions),
        ...(excludeActions.includes('posts') ? [] : postsActions),
        ...(excludeActions.includes('navigations') ? [] : navigationActions),
        ...(excludeActions.includes('adminSettings') ? [] : adminSettingsActions),
    ];
    const searcher = new FuzzySearch(actions.filter(action => !!action), ['name'], {
        caseSensitive: false,
    });
    const filteredActions = query ? searcher.search(query) : actions;
    const allActions = [
        ...filteredActions,
        ...(excludeActions.includes('search') ? [] : searchActions),
    ];
    // const sortedActions = filteredActions.sort((a, b) => {
    //   return a.category > b.category ? 1 : -1
    // })
    let lastCategory = null;
    return (_jsxs(_Fragment, { children: [actions
                .filter(action => action === null || action === void 0 ? void 0 : action.hotkey)
                .map(actionItem => (_jsx(CommandBarHotKey, { actionItem: actionItem }, actionItem.name))), isOpen && (_jsx(Modal, { open: true, onClose: onClose, size: "2xl", children: _jsxs(Combobox, { onChange: item => runAction(item), value: null, children: [(context === null || context === void 0 ? void 0 : context.entity) && (_jsx("div", { className: "p-3 pb-0", children: _jsx(Badge, { variant: "secondary", size: "lg", children: ((_a = context === null || context === void 0 ? void 0 : context.entity) === null || _a === void 0 ? void 0 : _a.title) ||
                                    ((_b = context === null || context === void 0 ? void 0 : context.entity) === null || _b === void 0 ? void 0 : _b.name) }, void 0) }, void 0)), _jsx("div", { className: "border-b border-line-subdued", children: _jsx(Combobox.Input, { as: Input, wrapperClassName: "shadow-none", leadingIcon: _jsx(SearchIcon, {}, void 0), className: "text-lg m-1 border-none focus:ring-transparent focus:border-0 focus-within:border-none", placeholder: intl.$t({
                                    id: 'Generics.SearchDotDotDot',
                                    defaultMessage: 'Search...',
                                }), onChange: event => setRawQuery(event.target.value) }, void 0) }, void 0), allActions.length > 0 && (_jsx(Combobox.Options, { static: true, className: "max-h-80 scroll-py-10 scroll-pb-2 overflow-y-auto py-4 pt-1", children: allActions.length > 0 && (_jsx(_Fragment, { children: allActions.map(action => {
                                    if (!action) {
                                        return null;
                                    }
                                    let showCategory = false;
                                    if (lastCategory !== (action === null || action === void 0 ? void 0 : action.category)) {
                                        showCategory = true;
                                        lastCategory = action === null || action === void 0 ? void 0 : action.category;
                                    }
                                    return (_jsxs(_Fragment, { children: [showCategory && (_jsx("li", { className: "text-content-subdued px-4 text-sm pb-1 pt-2", children: action === null || action === void 0 ? void 0 : action.category }, void 0)), _jsx(CommandBarAction, { action: action }, action.name)] }, void 0));
                                }) }, void 0)) }, void 0))] }, void 0) }, void 0)), postChildren, spaceChildren, memberChildren] }, void 0));
};
//# sourceMappingURL=CommandBar.js.map